import { memo, useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// The interactive/readout widget (slider, toggle, or latest-value display)
// for the current signal - kept as its own component so the LiveView modal
// can lay it out beside the graph (right column) instead of stacking it
// above/below. See DeviceGraph.jsx.
export const DeviceMonitorControlWidget = memo(
    ({
        signalTypeInfo,
        isExpanded,
        initialValue,
        onValueChange,
        inverted,
        isInterpretation,
    }) => {
        // Convert raw (wire) value to a percentage - "Inverted" is a wiring/
        // logic fact (e.g. a NC relay reads 1023 for physically OFF), not a
        // change to what the device puts on the wire, so it's applied here
        // (the interpreted %/ON-OFF reading) and undone in percentageToRaw
        // (so "Raw" always round-trips back to the true wire value, same
        // convention as the historical graph - see DeviceGraph.jsx).
        const rawToPercentage = (rawValue) => {
            const raw = rawValue || 0;
            const effective = inverted ? 1023 - raw : raw;
            return (effective / 1023) * 100;
        };

        // Convert percentage (0-100) to raw (wire) value
        const percentageToRaw = (percentage) => {
            const effective = Math.round((percentage / 100) * 1023);
            return inverted ? 1023 - effective : effective;
        };

        const [rangeValue, setRangeValue] = useState(
            rawToPercentage(initialValue)
        );
        const [toggleValue, setToggleValue] = useState(
            rawToPercentage(initialValue)
        );
        const sliderUpdateTimeout = useRef(null);

        // Update local state when initialValue changes
        useEffect(() => {
            const percentage = rawToPercentage(initialValue);
            setRangeValue(percentage);
            setToggleValue(percentage);
        }, [initialValue]);

        const handleRangeChange = (e) => {
            const percentageVal = parseInt(e.target.value);
            setRangeValue(percentageVal);

            if (sliderUpdateTimeout.current) {
                clearTimeout(sliderUpdateTimeout.current);
            }

            sliderUpdateTimeout.current = setTimeout(() => {
                const rawVal = percentageToRaw(percentageVal);
                onValueChange(rawVal);
            }, 500);
        };

        // Same slider as the analog control, but step="100" (only two
        // reachable positions) makes it snap straight to ON/OFF as it's
        // dragged instead of settling on an intermediate value.
        const handleDigitalSliderChange = (e) => {
            const percentageVal = parseInt(e.target.value);
            setToggleValue(percentageVal);
            onValueChange(percentageToRaw(percentageVal));
        };

        // A signal is only draggable/clickable when it's an output and not
        // also flagged as an input (matches the disabled= gating below) -
        // anything else is sense-only and should just display its latest
        // reading, not an interactive (but disabled) control.
        const canControlAnalog = signalTypeInfo.output && !signalTypeInfo.input;
        const canControlDigital = signalTypeInfo.output && !signalTypeInfo.input;
        const rawAnalogValue = percentageToRaw(rangeValue);
        const rawToggleValue = percentageToRaw(toggleValue);

        if (!isExpanded) return null;

        if (signalTypeInfo.analog && (signalTypeInfo.output || signalTypeInfo.input)) {
            return canControlAnalog ? (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-w-full">
                    <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        title={`${rangeValue}%`}
                        value={rangeValue}
                        onChange={handleRangeChange}
                        className="tw-w-full tw-h-2 tw-cursor-pointer tw-slider-thumb-custom"
                    />
                    <div className="tw-flex tw-justify-between tw-text-gray-500 tw-mt-2 tw-text-sm tw-w-full">
                        <span>0%</span>
                        <span className="tw-text-xs tw-text-gray-600">
                            {rangeValue.toFixed(1)}%
                        </span>
                        <span>100%</span>
                    </div>
                    <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
                        Raw: {rawAnalogValue}
                    </div>
                    <div className="tw-text-xs tw-text-gray-500 tw-italic tw-mt-1">
                        Drag to set the value
                    </div>
                </div>
            ) : (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-w-full">
                    <div className="tw-text-3xl tw-font-semibold tw-text-gray-800">
                        {rangeValue.toFixed(1)}%
                    </div>
                    <div className="tw-text-sm tw-text-gray-500 tw-mt-1">
                        Raw: {rawAnalogValue}
                    </div>
                    <div className="tw-text-xs tw-text-gray-500 tw-italic tw-mt-1">
                        Sense-only signal - showing latest reading
                    </div>
                </div>
            );
        }

        if (signalTypeInfo.digital && (signalTypeInfo.output || signalTypeInfo.input)) {
            return canControlDigital ? (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-w-full">
                    <input
                        type="range"
                        min="0"
                        max="100"
                        step="100"
                        title={toggleValue >= 50 ? "ON" : "OFF"}
                        value={toggleValue}
                        onChange={handleDigitalSliderChange}
                        className="tw-w-full tw-h-2 tw-cursor-pointer tw-slider-thumb-custom"
                    />
                    <div className="tw-flex tw-justify-between tw-text-gray-500 tw-mt-2 tw-text-sm tw-w-full">
                        <span>OFF</span>
                        <span className="tw-text-xs tw-text-gray-600 tw-font-medium">
                            {toggleValue >= 50 ? "ON" : "OFF"}
                        </span>
                        <span>ON</span>
                    </div>
                    <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
                        Raw: {rawToggleValue}
                    </div>
                    <div className="tw-text-xs tw-text-gray-500 tw-italic tw-mt-1">
                        Drag to toggle
                    </div>
                </div>
            ) : (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-w-full">
                    <div
                        className={`tw-text-3xl tw-font-semibold ${
                            toggleValue >= 50 ? "tw-text-green-600" : "tw-text-gray-500"
                        }`}
                    >
                        {toggleValue >= 50 ? "ON" : "OFF"}
                    </div>
                    <div className="tw-text-sm tw-text-gray-500 tw-mt-1">
                        Raw: {rawToggleValue}
                    </div>
                    <div className="tw-text-xs tw-text-gray-500 tw-italic tw-mt-1">
                        Sense-only signal - showing latest reading
                    </div>
                </div>
            );
        }

        // An interpretation carries a fixed BUS_VIRT type with no analog/
        // digital/sense/actuate bits of its own (see the backend's
        // flattenInterpretationsToOverrideEntries), so neither branch above
        // ever matches - but there's no "actuate an interpretation" concept
        // in this model either, only "read its decoded value", so it always
        // gets the same generic latest-reading readout a sense-only analog
        // signal gets.
        if (isInterpretation) {
            return (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-w-full">
                    <div className="tw-text-3xl tw-font-semibold tw-text-gray-800">
                        {rangeValue.toFixed(1)}%
                    </div>
                    <div className="tw-text-sm tw-text-gray-500 tw-mt-1">
                        Raw: {rawAnalogValue}
                    </div>
                    <div className="tw-text-xs tw-text-gray-500 tw-italic tw-mt-1">
                        Showing latest reading
                    </div>
                </div>
            );
        }

        return null;
    }
);

DeviceMonitorControlWidget.displayName = "DeviceMonitorControlWidget";

// Header row: prev/next nav, signal type/inverted/auxiliary badges, signal
// count. The control widget itself is rendered separately (see
// DeviceMonitorControlWidget above) so DeviceGraph.jsx can place it beside
// the graph instead of stacking it above/below.
const DeviceMonitorControl = memo(
    ({
        currentSignalKey,
        signalTypeInfo,
        isExpanded,
        currentSignalIndex,
        totalSignals,
        onPrevSignal,
        onNextSignal,
        isInverted,
        isAuxiliary,
    }) => {
        return (
            <div className={`tw-border-b ${isExpanded ? "tw-px-6 tw-py-4" : "tw-p-1"}`}>
                <div className="tw-flex tw-items-center tw-justify-between">
                    <button
                        className={`tw-rounded-full tw-text-white tw-transition-colors ${
                            isExpanded ? "tw-p-2" : "tw-p-1"
                        } ${
                            currentSignalIndex === 0
                                ? "tw-bg-gray-300 tw-cursor-not-allowed"
                                : "tw-bg-blue-500 hover:tw-bg-blue-600"
                        }`}
                        onClick={onPrevSignal}
                        disabled={currentSignalIndex === 0}
                    >
                        <ChevronLeft
                            className={`${
                                isExpanded ? "tw-h-6 tw-w-6" : "tw-h-4 tw-w-4"
                            }`}
                        />
                    </button>

                    <div
                        className={`tw-flex-1 tw-flex ${
                            isExpanded
                                ? "tw-flex-row tw-items-center tw-justify-between tw-px-6"
                                : "tw-flex-col tw-items-center"
                        }`}
                    >
                        {/* Signal type/inverted/auxiliary chips - the signal
                            name itself now lives in the modal's own title bar
                            (see Devices.jsx's onCurrentSignalChange), not
                            duplicated here. */}
                        <div className="tw-flex tw-flex-col tw-items-start tw-justify-center">
                            {!isExpanded && (
                                <div className="tw-font-bold tw-text-gray-800 tw-text-sm">
                                    {currentSignalKey}
                                </div>
                            )}
                            {isExpanded && (
                                <div className="tw-flex tw-flex-wrap tw-gap-2">
                                    {signalTypeInfo.analog && (
                                        <span className="tw-px-3 tw-py-1 tw-bg-purple-100 tw-text-purple-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                            <i className="fa fa-chart-line tw-me-1" />
                                            Proportional
                                        </span>
                                    )}
                                    {signalTypeInfo.digital && (
                                        <span className="tw-px-3 tw-py-1 tw-bg-green-100 tw-text-green-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                            <i className="fa fa-wave-square tw-me-1" />
                                            Discrete
                                        </span>
                                    )}
                                    {signalTypeInfo.input && (
                                        <span className="tw-px-3 tw-py-1 tw-bg-blue-100 tw-text-blue-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                            <i className="fa fa-eye tw-me-1" />
                                            Sense
                                        </span>
                                    )}
                                    {signalTypeInfo.output && (
                                        <span className="tw-px-3 tw-py-1 tw-bg-orange-100 tw-text-orange-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                            <i className="fa fa-bolt tw-me-1" />
                                            Actuate
                                        </span>
                                    )}
                                    {signalTypeInfo.status && (
                                        <span className="tw-px-3 tw-py-1 tw-bg-yellow-100 tw-text-yellow-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                            <i className="fa fa-info-circle tw-me-1" />
                                            Status
                                        </span>
                                    )}
                                    {signalTypeInfo.control && (
                                        <span className="tw-px-3 tw-py-1 tw-bg-red-100 tw-text-red-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                            <i className="fa fa-sliders-h tw-me-1" />
                                            Control
                                        </span>
                                    )}
                                    {isInverted !== undefined && (
                                        <span
                                            className={`tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-full ${
                                                isInverted
                                                    ? "tw-bg-green-100 tw-text-green-700"
                                                    : "tw-bg-gray-100 tw-text-gray-500"
                                            }`}
                                        >
                                            <i className="fa fa-retweet tw-me-1" />
                                            {isInverted ? "Inverted" : "Not inverted"}
                                        </span>
                                    )}
                                    {isAuxiliary !== undefined && (
                                        <span
                                            className={`tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-full ${
                                                isAuxiliary
                                                    ? "tw-bg-green-100 tw-text-green-700"
                                                    : "tw-bg-gray-100 tw-text-gray-500"
                                            }`}
                                        >
                                            <i className="fa fa-layer-group tw-me-1" />
                                            {isAuxiliary ? "Auxiliary" : "Not auxiliary"}
                                        </span>
                                    )}
                                    {signalTypeInfo.GPIO && (
                                        <span className="tw-px-3 tw-py-1 tw-bg-gray-100 tw-text-gray-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                            GPIO Bus
                                        </span>
                                    )}
                                    {signalTypeInfo.UART && (
                                        <span className="tw-px-3 tw-py-1 tw-bg-indigo-100 tw-text-indigo-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                            UART Bus
                                        </span>
                                    )}
                                    {signalTypeInfo.I2C && (
                                        <span className="tw-px-3 tw-py-1 tw-bg-teal-100 tw-text-teal-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                            I2C Bus
                                        </span>
                                    )}
                                    {signalTypeInfo.VIRTUAL && (
                                        <span className="tw-px-3 tw-py-1 tw-bg-slate-100 tw-text-slate-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                            Virtual
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Right side - Signal count */}
                        {isExpanded && totalSignals > 0 && (
                            <div className="tw-flex tw-items-center tw-text-sm tw-text-gray-500 tw-whitespace-nowrap">
                                {currentSignalIndex + 1} of {totalSignals} signals
                            </div>
                        )}
                    </div>

                    <button
                        className={`tw-rounded-full tw-text-white tw-transition-colors ${
                            isExpanded ? "tw-p-2" : "tw-p-1"
                        } ${
                            currentSignalIndex >= totalSignals - 1
                                ? "tw-bg-gray-300 tw-cursor-not-allowed"
                                : "tw-bg-blue-500 hover:tw-bg-blue-600"
                        }`}
                        onClick={onNextSignal}
                        disabled={currentSignalIndex >= totalSignals - 1}
                    >
                        <ChevronRight
                            className={`${
                                isExpanded ? "tw-h-6 tw-w-6" : "tw-h-4 tw-w-4"
                            }`}
                        />
                    </button>
                </div>
            </div>
        );
    }
);

DeviceMonitorControl.displayName = "DeviceMonitorControl";

export default DeviceMonitorControl;
